import React from 'react';
import styles from './RangeSlider.module.css';

interface RangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  step?: number;
  formatLabel?: (value: number) => string;
  onChange: (value: [number, number]) => void;
}

export function RangeSlider({ min, max, value, step = 1, formatLabel, onChange }: RangeSliderProps) {
  const [low, high] = value;

  const range = max - min;
  const leftPct  = ((low  - min) / range) * 100;
  const rightPct = ((max  - high) / range) * 100;

  // When both thumbs meet at max, the low thumb must sit on top so it can be dragged back down.
  const lowZIndex = low === high ? 2 : 1;

  function handleLow(e: React.ChangeEvent<HTMLInputElement>) {
    const v = Math.min(Number(e.target.value), high);
    onChange([v, high]);
  }

  function handleHigh(e: React.ChangeEvent<HTMLInputElement>) {
    const v = Math.max(Number(e.target.value), low);
    onChange([low, v]);
  }

  const lowLabel  = formatLabel ? formatLabel(low)  : String(low);
  const highLabel = formatLabel ? formatLabel(high) : String(high);

  return (
    <div className={styles.root}>
      <div className={styles.trackWrapper}>
        {/* Visual track + fill */}
        <div className={styles.track}>
          <div
            className={styles.fill}
            style={{ left: `${leftPct}%`, right: `${rightPct}%` }}
          />
        </div>

        {/* Low handle */}
        <input
          type="range"
          className={styles.input}
          style={{ zIndex: lowZIndex }}
          min={min}
          max={max}
          step={step}
          value={low}
          onChange={handleLow}
          aria-label="Minimum value"
        />

        {/* High handle */}
        <input
          type="range"
          className={styles.input}
          style={{ zIndex: lowZIndex === 2 ? 1 : 2 }}
          min={min}
          max={max}
          step={step}
          value={high}
          onChange={handleHigh}
          aria-label="Maximum value"
        />
      </div>

      <div className={styles.labels}>
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  );
}
